/**
 * Floating Agent
 * 
 * @Author: Focci
 * @Date: 2023-08-11 10:55:43
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-11 10:55:43
 */

'use client'

import PropTypes from 'prop-types'
import RouteBack from '@comp/RouteBack'
import useTracking from '@hook/useTracking'
import clsx from 'clsx'
import AgentEnquiry from '@comp/dialog/agent-enquiry'
import FavoriteAndShareSimple from '@page/property/favorite-and-share/simple'
import CloseIcon from '@mui/icons-material/Close'
import AspectRatioCover from '@comp/AspectRatioCover'
import SVGPhone from '@img/phone.svg'
import SVGEmail from '@img/email.svg'
import useProgressRouter from '@hook/useProgressRouter'
import { useCallback, useMemo, useState } from 'react'
import { isArray } from 'lodash-es'
import { ButtonBase } from '@comp/material'
import { useTranslations } from 'next-intl'
import { getEnquiryProps } from '../agent-card/lib'

function PhoneAndEmailButton({
  icon,
  href = '',
  label = '',
  Component = 'a',
  className = 'bg-white text-info',
  onClick = () => {}
}) {
  return (
    <ButtonBase className="rounded-full" onClick={onClick}>
      <Component
        {...(Component === 'a' ? {
          href,
          'data-noprogress': '1',
        } : {})} 
        className={clsx([
          'h-6 px-3 flex items-center gap-1 justify-center rounded-full',
          className
        ])}
      >
        {icon}
        <span className="whitespace-nowrap text-f.8">
          {label}
        </span>
      </Component>
    </ButtonBase>
  )
}
export default function FloatingAgentBar({
  house = {},
  isModal,
}) {
  const tc = useTranslations('common')
  const ts = useTranslations('search')
  const { routerBack } = useProgressRouter()
  
  const [open, setOpen] = useState(false)
  const { pushTrackListing, pushTrack } = useTracking()
  const agent = useMemo(() => (isArray(house.agents) ? house.agents[0] : null), [house.agents])

  const enquiryProps = getEnquiryProps(house)

  const closeDialog = useCallback(() => setOpen(false), [])
  const handlePhoneEmailClick = useCallback((type) => {
    const isEmail = type === 'email'
    
    isEmail && setOpen(true)

    const eventName = isEmail ? 'click_email' : 'click_phone'
    pushTrackListing(eventName, house)
    pushTrack(eventName, {
      houseId: house.houseId || 0,
      agentId: agent?.id,
      agencyOfficeId: agent?.office_id
    })
  }, [agent?.id, agent?.office_id, house, pushTrack, pushTrackListing])
  
  return (
    <>
      <div className="flex items-center justify-between md:justify-around gap-x-8 gap-y-4 text-white p-[0.375rem]">
        <div className="shrink-0 flex items-center gap-8 pl-2 w-auto 2xl:w-48">
          <RouteBack className="" />
        </div>
        <div className="mx-auto hidden md:flex items-center gap-x-8 w-274 max-w-full">
          <div className="flex items-center flex-auto gap-8">
            <div className="flex-auto">
              {String(house.address).split(',')[0]}
              <div className="text-f.8">
                {house.priceLabel}
              </div>
            </div>
            <div className="flex-auto hidden xl:flex items-center gap-4">
              <div className="flex flex-col flex-auto">
                {ts('bedrooms')}
                <b className="text-f.8">
                  {(!house.bedrooms || house.bedrooms === '0') ? '-' : house.bedrooms}
                </b>
              </div>
              <div className="flex flex-col flex-auto">
                {ts('bathrooms')}
                <b className="text-f.8">
                  {(!house.bathrooms || house.bathrooms === '0') ? '-' : house.bathrooms}
                </b>
              </div>
              <div className="flex flex-col flex-auto">
                {ts('carspaces')}
                <b className="text-f.8">
                  {(!house.carspaces || house.carspaces === '0') ? '-' : house.carspaces}
                </b>
              </div>
            </div>
          </div>
          <div 
            className={clsx([
              'flex-auto hidden md:flex items-center gap-4 w-auto 2xl:w-48',
              !agent && 'justify-end'
            ])}
          >
            {agent && (
              <>
                <div className="w-8">
                  <AspectRatioCover
                    subtile
                    brand=""
                    wrapperClassName="rounded-full"
                    className="rounded-full object-cover"
                    src={agent.oss_avatar}
                    width={40}
                    height={40}
                    alt={agent.first_name + agent.last_name}
                  />
                </div>
                <div className="text-base font-medium">
                  {`${agent.first_name} ${agent.last_name}`}
                </div>
                {agent.phone_numbers?.mobile && (
                  <PhoneAndEmailButton
                    label={tc('phone')}
                    icon={<SVGPhone width=".85rem" height=".85rem" />}
                    href={`tel:${agent.phone_numbers.mobile}`}
                    onClick={() => handlePhoneEmailClick('phone')}
                  />
                )}
                {/* 后端邮件字段返回的是 login ........ NB! */}
                {agent.login && (
                  <PhoneAndEmailButton
                    Component="div"
                    label={tc('email')}
                    icon={<SVGEmail />}
                    className="bg-primary text-white"
                    onClick={() => handlePhoneEmailClick('email')}
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-8">
          <FavoriteAndShareSimple house={house} />
        </div>
        {isModal && (
          <ButtonBase className="rounded-full -ml-6" onClick={routerBack}>
            <div className="flex items-center px-2 py-0.5">
              <CloseIcon sx={{ fontSize: '1rem' }} />
              <span className="whitespace-nowrap">{tc('close')}</span>
            </div>
          </ButtonBase>
        )}
      </div>
      <AgentEnquiry 
        house={house}
        open={open}
        onClose={closeDialog}
        onSuccess={closeDialog}
        {...enquiryProps}
      />
    </>
  )
}

FloatingAgentBar.propTypes = {
  house: PropTypes.object,
}
